import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllItems } from '../../utils/itemOperations';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';

const LiveStatusGrid = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'available', 'checked-out', 'reserved'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadEquipmentStatus();
  }, []);

  const loadEquipmentStatus = async () => {
    try {
      setLoading(true);
      setError('');

      // Get all items
      const allItems = await getAllItems();
      
      // Get active reservations for each item
      const itemsWithReservations = await Promise.all(
        allItems.map(async (item) => {
          try {
            const reservationsRef = collection(db, 'items', item.id, 'reservations');
            const activeQuery = query(
              reservationsRef,
              where('status', 'in', ['reserved', 'checked_out'])
            );
            const reservationsSnap = await getDocs(activeQuery);
            
            const activeReservations = [];
            const now = new Date();
            
            reservationsSnap.forEach((doc) => {
              const data = doc.data();
              const endTime = data.end instanceof Object && data.end.toDate ? data.end.toDate() : new Date(data.end);
              
              // Only include non-expired reservations
              if (endTime >= now) {
                activeReservations.push({ id: doc.id, ...data });
              }
            });

            // Determine effective status
            let effectiveStatus = item.status;
            let reservationInfo = null;
            
            if (activeReservations.length > 0) {
              // Sort by start time to find current/next reservation
              activeReservations.sort((a, b) => {
                const aStart = a.start instanceof Object && a.start.toDate ? a.start.toDate() : new Date(a.start);
                const bStart = b.start instanceof Object && b.start.toDate ? b.start.toDate() : new Date(b.start);
                return aStart - bStart;
              });
              
              const currentReservation = activeReservations.find(r => {
                const startTime = r.start instanceof Object && r.start.toDate ? r.start.toDate() : new Date(r.start);
                const endTime = r.end instanceof Object && r.end.toDate ? r.end.toDate() : new Date(r.end);
                return startTime <= now && endTime >= now;
              });
              
              if (currentReservation) {
                effectiveStatus = currentReservation.status === 'checked_out' ? 'Checked Out' : 'Reserved';
                reservationInfo = currentReservation;
              } else {
                // Upcoming reservation
                const upcomingReservation = activeReservations.find(r => {
                  const startTime = r.start instanceof Object && r.start.toDate ? r.start.toDate() : new Date(r.start);
                  return startTime > now;
                });
                
                if (upcomingReservation && item.status === 'Available') {
                  effectiveStatus = 'Reserved';
                  reservationInfo = upcomingReservation;
                }
              }
            }

            return {
              ...item,
              effectiveStatus,
              reservationInfo,
              activeReservations
            };
          } catch (error) {
            console.error(`Error loading reservations for item ${item.id}:`, error);
            return {
              ...item,
              effectiveStatus: item.status,
              reservationInfo: null,
              activeReservations: []
            };
          }
        })
      );

      setItems(itemsWithReservations);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(itemsWithReservations.map(item => item.category).filter(Boolean))];
      setCategories(uniqueCategories.sort());
      
    } catch (error) {
      console.error('Error loading equipment status:', error);
      setError('Failed to load equipment status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Available':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Checked Out':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Reserved':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Unavailable':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Available':
        return '';
      case 'Checked Out':
        return '';
      case 'Reserved':
        return '';
      case 'Unavailable':
        return '';
      default:
        return '';
    }
  };

  const filteredItems = items.filter(item => {
    // Status filter
    if (filter !== 'all') {
      const statusMatch = {
        'available': item.effectiveStatus === 'Available',
        'checked-out': item.effectiveStatus === 'Checked Out',
        'reserved': item.effectiveStatus === 'Reserved'
      };
      if (!statusMatch[filter]) return false;
    }
    
    // Category filter
    if (categoryFilter !== 'all' && item.category !== categoryFilter) {
      return false;
    }
    
    return true;
  });

  const getStatusCounts = () => {
    const counts = {
      available: 0,
      checkedOut: 0,
      reserved: 0,
      total: items.length
    };

    items.forEach(item => {
      switch (item.effectiveStatus) {
        case 'Available':
          counts.available++;
          break;
        case 'Checked Out':
          counts.checkedOut++;
          break;
        case 'Reserved':
          counts.reserved++;
          break;
      }
    });

    return counts;
  };

  const statusCounts = getStatusCounts();

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-lg text-gray-600">Loading equipment status...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="bg-red-100 border-l-4 border-red-500 p-4">
          <div className="text-red-700">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 lg:mb-0">
          Live Equipment Status
        </h2>
        <button
          onClick={loadEquipmentStatus}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
        >
          Refresh
        </button>
      </div>
      
      {/* Status Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="text-center p-3 bg-gray-50 rounded-lg">
          <div className="text-2xl font-bold text-gray-700">{statusCounts.total}</div>
          <div className="text-sm text-gray-600">Total Equipment</div>
        </div>
        <div className="text-center p-3 bg-green-50 rounded-lg">
          <div className="text-2xl font-bold text-green-700">{statusCounts.available}</div>
          <div className="text-sm text-green-600">Available</div>
        </div>
        <div className="text-center p-3 bg-orange-50 rounded-lg">
          <div className="text-2xl font-bold text-orange-700">{statusCounts.checkedOut}</div>
          <div className="text-sm text-orange-600">Checked Out</div>
        </div>
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <div className="text-2xl font-bold text-blue-700">{statusCounts.reserved}</div>
          <div className="text-sm text-blue-600">Reserved</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status ({filteredItems.length})</option>
            <option value="available">Available ({statusCounts.available})</option>
            <option value="checked-out">Checked Out ({statusCounts.checkedOut})</option>
            <option value="reserved">Reserved ({statusCounts.reserved})</option>
          </select>
        </div>
        
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Category</label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>{category}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Equipment Grid */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No equipment found matching the selected filters.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.map(item => (
            <Link
              key={item.id}
              to={`/item/${item.id}`}
              className="block border rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 truncate" title={item.name}>
                    {item.name}
                  </h3>
                  <p className="text-sm text-gray-500 truncate" title={item.category}>
                    {item.category || 'Uncategorized'}
                  </p>
                </div>
                <div className="ml-2 text-lg">
                  {getStatusIcon(item.effectiveStatus)}
                </div>
              </div>
              
              <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(item.effectiveStatus)}`}>
                {item.effectiveStatus}
              </div>
              
              {item.reservationInfo && (
                <div className="mt-3 pt-3 border-t text-xs text-gray-600">
                  <div className="font-medium">
                    {item.reservationInfo.userName || `PID: ${item.reservationInfo.pid}`}
                  </div>
                  <div>
                    Until: {new Date(item.reservationInfo.end instanceof Object && item.reservationInfo.end.toDate ? item.reservationInfo.end.toDate() : item.reservationInfo.end).toLocaleString()}
                  </div>
                </div>
              )}
              
              {item.effectiveStatus === 'Checked Out' && !item.reservationInfo && (
                <div className="mt-3 pt-3 border-t text-xs text-gray-600">
                  <div className="font-medium">{item.currentHolderName}</div>
                  {item.dueDate && (
                    <div>Due: {new Date(item.dueDate).toLocaleString()}</div>
                  )}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveStatusGrid;

